import type { ComponentType } from 'react';
import React, { useMemo } from 'react';
import type { JsonLd } from 'json-ld-types';

import { type CardState, getStatus } from '@atlaskit/linking-common';

import { expVal } from '@confluence/feature-experiments';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useStoreOptions as useStoreOptionsOld } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';

const useInitialState = () => {
	return useMemo(() => {
		const ssrProvidersData: { editorCardProvider?: Record<string, JsonLd.Response> } =
			window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] || {};
		const ssrData = ssrProvidersData.editorCardProvider;
		if (!ssrData) {
			return undefined;
		}

		return Object.entries(ssrData).reduce<Record<string, CardState>>((acc, [url, details]) => {
			acc[url] = {
				status: getStatus(details),
				details,
				metadataStatus: 'resolved',
			};

			return acc;
		}, {});
	}, []);
};

const useStoreOptions = () => {
	const isPageSSR = isPageSSRd();
	const initialState = useInitialState();
	const oldInitialState = window['__SSR_SMARTLINKS__'];
	const oldStoreOptions = useStoreOptionsOld(oldInitialState, isPageSSR);

	return useMemo(() => {
		if (initialState && expVal('platform_editor_smart_card_otp', 'isEnabled', false)) {
			return {
				initialState,
			};
		}
		// Should be removed when the platform_editor_smart_card_otp experiment is rolled out
		return oldStoreOptions.storeOptions;
	}, [oldStoreOptions, initialState]);
};

export function withSmartCardProviderContext<T>(WrappedComponent: ComponentType<T>) {
	return (props: any) => {
		const { isAdminHubAIEnabled } = useSessionData();

		const storeOptions = useStoreOptions();

		const content = <WrappedComponent {...props} />;

		return (
			<>
				<ConfluenceSmartCardProvider
					{...getSmartCardRenderers()}
					storeOptions={storeOptions}
					isAdminHubAIEnabled={isAdminHubAIEnabled}
				>
					{content}
				</ConfluenceSmartCardProvider>
			</>
		);
	};
}
